import React from "react";
import { ArrowRightIcon, CheckIcon, LayoutIcon, SparklesIcon } from "../Icons";
import { CONTAINER, GRID_BACKDROP, PrimaryLink, SecondaryLink } from "../ui";

const JOURNEY_STEPS = ["Goals captured", "Mentors matched", "Sessions tracked"];
const ENGAGEMENT_BARS = [42, 68, 56, 84, 72, 94];

function ProgrammeMock() {
  return (
    <div className="tw-relative tw-mx-auto tw-w-full tw-max-w-[520px] tw-ml-auto sm:tw-mx-auto">
      <div className="tw-pointer-events-none tw-absolute -tw-left-12 tw-top-20 tw-h-40 tw-w-40 tw-rounded-full tw-bg-[#a8dcff]/40 tw-blur-3xl" />
      <div className="tw-pointer-events-none tw-absolute -tw-right-10 -tw-top-8 tw-h-44 tw-w-44 tw-rounded-full tw-bg-[#68d7cc]/25 tw-blur-3xl" />

      <div className="tw-relative tw-rounded-[28px] tw-border tw-border-[#c9deef] tw-bg-white tw-p-4 tw-rotate-[1.25deg] tw-shadow-[0_32px_80px_rgba(23,72,111,.18)] sm:tw-rotate-0 sm:tw-p-3">
        <div className="tw-rounded-[20px] tw-bg-[#10233f] tw-p-5 tw-text-white sm:tw-p-4">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <p className="tw-m-0 tw-text-xs tw-font-bold tw-uppercase tw-tracking-[.16em] tw-text-[#8fcfff]">
                Programme overview
              </p>
              <p className="tw-mt-1 tw-mb-0 tw-text-lg tw-font-bold">Leadership mentoring cohort</p>
            </div>
            <LayoutIcon aria-hidden className="tw-ml-2 tw-h-6 tw-w-6 tw-shrink-0 tw-text-[#55cfc1]" />
          </div>

          <div className="tw-mt-6 tw-grid tw-grid-cols-3 tw-gap-3 sm:tw-gap-2">
            {[
              ["Onboard", "✓"],
              ["Match", "✓"],
              ["Engage", "Live"],
            ].map(([label, state]) => (
              <div
                key={label}
                className="tw-min-w-0 tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[.07] tw-px-3 tw-py-3 sm:tw-px-2"
              >
                <p className="tw-m-0 tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-[#9fb5ca]">
                  {label}
                </p>
                <p className="tw-mt-1 tw-mb-0 tw-text-xl tw-font-black tw-text-white">{state}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-[1fr_.9fr] tw-gap-3 tw-p-2 tw-pt-5 xs:tw-grid-cols-1">
          <div className="tw-rounded-2xl tw-bg-[#eff7fd] tw-p-4">
            <p className="tw-m-0 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-[#4d6d8a]">
              Participant journey
            </p>
            <div className="tw-mt-4 tw-space-y-4">
              {JOURNEY_STEPS.map((step, index) => (
                <div key={step} className="tw-flex tw-items-center tw-gap-3">
                  <span className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#087bd3] tw-text-xs tw-font-black tw-text-white">
                    {index + 1}
                  </span>
                  <span className="tw-text-sm tw-font-bold tw-text-[#243b55]">{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="tw-rounded-2xl tw-border tw-border-[#dbe8f2] tw-p-4">
            <p className="tw-m-0 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-[#4d6d8a]">
              HR visibility
            </p>
            <div
              className="tw-mt-5 tw-flex tw-items-end tw-gap-2"
              role="img"
              aria-label="Illustration of rising session engagement over six months"
            >
              {ENGAGEMENT_BARS.map((height, index) => (
                <span
                  key={index}
                  className="tw-w-full tw-rounded-t-md tw-bg-[#55cfc1]"
                  style={{ height }}
                />
              ))}
            </div>
            <p className="tw-mt-3 tw-mb-0 tw-text-sm tw-font-bold tw-text-[#243b55]">
              Six months of session activity.
            </p>
          </div>
        </div>
      </div>

      <div className="tw-absolute -tw-bottom-7 -tw-left-10 tw-rounded-2xl tw-border tw-border-[#c8dfef] tw-bg-white tw-px-5 tw-py-4 tw-shadow-xl sm:-tw-left-4">
        <p className="tw-m-0 tw-text-2xl tw-font-black tw-text-[#087bd3]">4.3 / 5</p>
        <p className="tw-m-0 tw-text-xs tw-font-bold tw-text-[#60768c]">Average mentor rating</p>
      </div>
    </div>
  );
}

export default function Hero({
  promise,
  businessCaseUrl,
}: {
  promise: { hours: number };
  businessCaseUrl: string;
}) {
  return (
    <section id="top" className="tw-relative tw-bg-[#f4f8fc] tw-pb-24 tw-pt-20 sm:tw-pb-16 sm:tw-pt-12">
      <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-40" style={GRID_BACKDROP} />

      <div
        className={`${CONTAINER} tw-relative tw-grid tw-grid-cols-[1.08fr_.92fr] tw-items-center tw-gap-14 sm:tw-grid-cols-1 sm:tw-gap-10`}
      >
        <div>
          <div className="tw-mb-7 tw-inline-flex tw-max-w-full tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-[#b9d9f1] tw-bg-white tw-px-4 tw-py-2 tw-text-xs tw-font-bold tw-uppercase tw-leading-5 tw-tracking-[.14em] tw-text-[#087bd3] tw-shadow-sm sm:tw-mb-5 sm:tw-rounded-2xl sm:tw-px-3 sm:tw-text-[10px] sm:tw-tracking-[.1em]">
            <SparklesIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
            HolonIQ Top 50 EdTech · Workplace Learning · 2020–2025
          </div>

          {/* The brand face is wider than the reference's, so the display size is stepped down to
              fill the column in the same number of lines. */}
          <h1 className="tw-m-0 tw-max-w-3xl tw-text-[clamp(2.75rem,5.1vw,4.5rem)] tw-font-black tw-leading-[1] tw-tracking-[-.045em] tw-text-[#10233f] sm:tw-text-[clamp(2.25rem,8vw,2.75rem)] sm:tw-leading-[1.06]">
            Turn mentoring into a programme people use — and leaders can measure.
          </h1>

          <p className="tw-mt-7 tw-mb-0 tw-max-w-2xl tw-text-xl tw-leading-8 tw-text-[#536a82] sm:tw-mt-5 sm:tw-text-base sm:tw-leading-7">
            FutureLab helps HR and L&amp;D teams design, launch, manage and measure structured
            mentoring and coaching programmes, from one cohort to organisation-wide scale.
          </p>

          <div className="tw-mt-9 tw-flex tw-flex-row tw-items-center tw-gap-3 sm:tw-mt-6 sm:tw-flex-col sm:tw-items-stretch">
            <PrimaryLink href={businessCaseUrl}>
              Build your business case
              <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4" />
            </PrimaryLink>
            <SecondaryLink href="#how-it-works">See how it works</SecondaryLink>
          </div>

          <p className="tw-mt-5 tw-mb-0 tw-flex tw-items-start tw-gap-2 tw-text-sm tw-font-medium tw-leading-6 tw-text-[#61758a]">
            <CheckIcon aria-hidden className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#0f7a62]" />
            Backed by our {promise.hours}-hour admin-saving promise for eligible cohorts.
          </p>
        </div>

        <ProgrammeMock />
      </div>
    </section>
  );
}
